import React, { ComponentProps, ReactElement, ReactNode, Ref } from 'react';

import { CSSProp } from 'styled-components';

import ControlLabel from './ControlLabel';
import Placeholder from './Placeholder';
import { SearchInputProps } from './SearchInput';
import Selected from './Selected';
import { defaultLabelGetter } from '../../helpers/defaultGetters';
import {
  DefaultItem,
  GenericItem,
  GetInputProps,
  GetSelectedItemProps,
  GetToggleButtonProps,
  ItemToString,
} from '../../types';

export type MultiControlProps<TItem extends GenericItem> = ComponentProps<
  typeof ControlLabel
> &
  Partial<GetInputProps> &
  GetToggleButtonProps & {
    getSelectedItemProps: GetSelectedItemProps<TItem>;
    isLoading?: boolean;
    selectedItems: TItem[] | null;
    removeSelectedItem: (item: TItem) => void;
    itemToString?: ItemToString<TItem>;
    placeholder?: ReactNode;
    renderInput?: (props: SearchInputProps) => ReactNode;
    renderItems?: ({
      isOpen,
      selectedItems,
      getSelectedItemProps,
      removeSelectedItem,
      itemToString,
    }: {
      getSelectedItemProps: GetSelectedItemProps<TItem>;
      isOpen: boolean;
      selectedItems: TItem[];
      removeSelectedItem: (item: TItem) => void;
      itemToString: ItemToString<TItem>;
    }) => ReactNode;
    renderPlaceholder?: (
      props?: ComponentProps<typeof Placeholder>,
    ) => ReactNode;
  };

const MultiControlWrapped = <TItem extends GenericItem>(
  {
    isOpen = false,
    selectedItems,
    getSelectedItemProps,
    removeSelectedItem,
    getInputProps,
    itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
    placeholder,
    renderInput: renderInputProp,
    renderItems = () => (
      <Selected selectedItems={selectedItems} itemToString={itemToString} />
    ),
    renderPlaceholder = (placeholderProps) => (
      <Placeholder {...placeholderProps} />
    ),
    getToggleButtonProps,
    disabled,
    ...props
  }: MultiControlProps<TItem>,
  ref: Ref<HTMLDivElement | HTMLLabelElement>,
) => {
  const hasSelectedItems = !!selectedItems && !!selectedItems.length;

  const renderInput = () => {
    if (!renderInputProp || !getInputProps) {
      return null;
    }

    const { style, ...inputProps } = getInputProps({
      readOnly: !isOpen,
      disabled,
    });

    return renderInputProp({
      ...inputProps,
      style: style as CSSProp,
      isOpen,
    });
  };

  return (
    <ControlLabel
      ref={ref}
      isOpen={isOpen}
      getToggleButtonProps={getToggleButtonProps}
      disabled={disabled}
      {...props}
    >
      {hasSelectedItems &&
        renderItems &&
        renderItems({
          isOpen,
          selectedItems,
          getSelectedItemProps,
          removeSelectedItem,
          itemToString,
        })}
      {renderInput()}
      {!hasSelectedItems && renderPlaceholder({ children: placeholder })}
    </ControlLabel>
  );
};

const MultiControl = React.forwardRef(MultiControlWrapped);

MultiControl.displayName = 'MultiControl';

MultiControl.propTypes = {
  ...ControlLabel.propTypes,
};

export default MultiControl as <T extends GenericItem>(
  p: MultiControlProps<T> & { ref?: Ref<HTMLDivElement | HTMLLabelElement> },
) => ReactElement;
